/**
 * SelectFilter — per-column multi-select filter.
 *
 * Round 5 of the per-filter design-system migration. Replaces the
 * MUI <Select multiple> + custom MenuPaper trigger with a
 * `<FormPopover>` (Tier 3) that hosts a checkbox list, plus a
 * trigger button styled like the rest of the inline filter row
 * (reusing `.qhs-date-field` classes for visual parity — same
 * height, border, focus / hover / highlighted / disabled).
 *
 * Behaviour preserved 1:1 vs. legacy:
 *   - `isFilterable: false` disables the trigger.
 *   - `options` may be flat or carry parent/child sub-arrays
 *     (`option.child = ['a', 'b']` makes the parent select-all
 *     for those values; `option.isChild = true` indents in the
 *     list).
 *   - Selecting the synthetic "All" option (value=`''`) wipes the
 *     filter back to empty.
 *   - Selected values are committed only on Apply (Tier 3 buffered
 *     draft pattern). Cancel / outside-click discards the draft.
 *   - Clear all resets the draft to empty without closing the
 *     popover, matching the design-system Tier 3 head action.
 *   - Status badges (column key contains `status` or equals
 *     `patientGender`) and tag badges (key in the curated list
 *     below) render alongside the option label.
 *
 * Badge sourcing: renders FSD `<Badge size="sm" tone={...}>` directly.
 * `getStatusBadge` and `getTagsColor` were migrated to return FSD
 * tone names ('normal' / 'critical' / 'sage' / …) so the helpers
 * and primitive talk the same vocabulary. Compact `size="sm"`
 * keeps the chip from dominating the option row in narrow column
 * popovers.
 */
import cx from 'classnames';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import Badge from '@shared/ui/Badge';
import getStatusBadge, { getTagsColor } from '@shared/lib/getStatusBadge';
import FormPopover from '@shared/ui/Popover/FormPopover';
import { normaliseSelectValue, describeSelectValue } from '../lib/describeFilter';

const ChevronSvg = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

const CheckSvg = (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="3"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Column keys that render their values as tag-style coloured pills.
// Must stay in lockstep with the legacy detection: MRT columns can
// pass `withBubble: true` to opt in explicitly for any other key.
const TAG_KEYS = new Set(['accountCustomTags', 'customTags', 'patientCustomTags', 'surveyTags']);

// Tone -> sort priority. Lower number floats to the top of the
// dropdown. Designed so the visually most-important rows lead:
// `critical` (urgent action) and `normal` (positive completed
// outcome) sit near the top, with terminal `inactive` always last.
// Category tags follow at the end in palette order — tags carry no
// severity, so only their stable order matters.
const TONE_PRIORITY = {
  critical: 1,
  normal: 2,
  pending: 3,
  high: 4,
  moderate: 5,
  inactive: 6,
  slate: 10,
  sage: 11,
  indigo: 12,
  stone: 13,
  wine: 14,
  graphite: 15,
};

// Sort options by their resolved tone. Skips sorting if any option
// declares parent/child grouping — children must stay adjacent to
// their parent and we don't try to re-thread the grouping here.
const sortByTone = (options, getTone) => {
  if (options.some(o => o.child)) return options;
  return [...options].sort((a, b) => {
    const pa = TONE_PRIORITY[getTone(a.value)] ?? 99;
    const pb = TONE_PRIORITY[getTone(b.value)] ?? 99;
    return pa - pb;
  });
};

// Toggle a value or a parent-with-children set against the draft
// array. Returns the next draft.
const toggle = (draft, option) => {
  // "All" — wipe selection
  if (option.value === '') return [];

  // Parent: select / deselect every child
  if (option.child) {
    const allSelected = option.child.every(c => draft.includes(c));
    if (allSelected) {
      return draft.filter(v => !option.child.includes(v));
    }
    return [...draft, ...option.child.filter(c => !draft.includes(c))];
  }

  // Regular item
  if (draft.includes(option.value)) {
    return draft.filter(v => v !== option.value);
  }
  return [...draft, option.value];
};

const isOptionChecked = (draft, option) => {
  if (option.value === '') {
    return draft.length === 0;
  }
  if (option.child) {
    return option.child.every(c => draft.includes(c));
  }
  return draft.includes(option.value);
};

const SelectFilter = ({
  filterValue = [],
  dataKey,
  columnData: { isFilterable, options = [], withBubble, preserveOptionOrder },
  onChange,
}) => {
  const { tables, btn } = useSelector(state => state.i18n);
  const [open, setOpen] = useState(false);

  // Normalise the filter value coming back from MRT — it can arrive
  // as an array or a comma-separated string. Empty / unfiltered
  // states are represented as `[]` internally.
  const normalised = useMemo(() => normaliseSelectValue(filterValue), [filterValue]);

  // Buffered draft. Re-seed on open so external clear-all flows
  // through and the user starts each session from the committed
  // value, not whatever they cancelled out of last time.
  const [draft, setDraft] = useState(normalised);
  useEffect(() => {
    if (open) setDraft(normalised);
    // eslint-disable-next-line
  }, [open]);

  const isStatus = dataKey.toLowerCase().includes('status') || dataKey === 'patientGender';
  const showBubble = withBubble || TAG_KEYS.has(dataKey) || isStatus;

  // Sort by tone severity when the column shows badges; otherwise
  // preserve the order the column passed in. A column whose source
  // order is meaningful (e.g. the assessment status lifecycle) opts
  // out via `filter.preserveOptionOrder`.
  const sortedOptions = useMemo(() => {
    if (!showBubble || preserveOptionOrder) return options;
    const getTone = TAG_KEYS.has(dataKey) ? getTagsColor : getStatusBadge;
    return sortByTone(options, getTone);
  }, [options, showBubble, dataKey, preserveOptionOrder]);

  const allOptions = useMemo(
    () => [{ label: tables?.all || 'All', value: '' }, ...sortedOptions],
    [tables, sortedOptions]
  );

  // Flat list of "leaf" values currently selected, used for the
  // "N selected" count and the Apply-button label.
  const draftLeafCount = draft.length;

  // Trigger label — comma-joined labels of every selected value.
  // The trigger button has `text-overflow: ellipsis` (via the
  // shared `.qhs-date-field__value`), so a label that doesn't fit
  // truncates with `…` and the user can read the full set in the
  // popover. More informative at a glance than a bare count, but
  // never overflows.
  const triggerLabel = useMemo(() => {
    if (normalised.length === 0) return tables?.all || 'All';
    return describeSelectValue(normalised, options);
  }, [normalised, options, tables]);

  const isFiltered = normalised.length > 0;

  const handleApply = () => {
    onChange?.(dataKey, draft);
    setOpen(false);
  };

  const handleCancel = () => {
    setOpen(false);
  };

  const handleClearAll = () => {
    setDraft([]);
  };

  const handleToggleOption = option => {
    setDraft(prev => toggle(prev, option));
  };

  const trigger = (
    <button
      type="button"
      onClick={() => isFilterable && setOpen(o => !o)}
      disabled={!isFilterable}
      className={cx('qhs-date-field', 'qhs-date-field--sm', 'qhs-date-field--full', {
        'qhs-date-field--placeholder': !isFiltered,
        'qhs-date-field--highlighted': isFiltered,
      })}
    >
      <span className="qhs-date-field__value">{triggerLabel}</span>
      <span className="qhs-date-field__icon">{ChevronSvg}</span>
    </button>
  );

  return (
    <FormPopover
      open={open}
      onClose={handleCancel}
      trigger={trigger}
      title={tables?.filter || 'Filter'}
      headAction={
        draft.length > 0
          ? { label: btn?.clearFilter || 'Clear all', onClick: handleClearAll }
          : undefined
      }
      onApply={handleApply}
      onCancel={handleCancel}
      applyLabel={
        draftLeafCount > 0 ? `${btn?.apply || 'Apply'} (${draftLeafCount})` : btn?.apply || 'Apply'
      }
      cancelLabel={btn?.cancel || 'Cancel'}
    >
      {allOptions.map(option => {
        const checked = isOptionChecked(draft, option);
        return (
          <button
            key={option.value || 'all'}
            type="button"
            className={cx(
              'qhs-pop__item',
              { 'is-checked': checked },
              option.isChild && 'qhs-pop__item--child'
            )}
            onClick={() => handleToggleOption(option)}
            style={option.isChild ? { paddingLeft: 32 } : undefined}
          >
            <span
              className={cx('qhs-pop__item-ctrl', { 'is-checked': checked })}
              aria-hidden="true"
            >
              {CheckSvg}
            </span>
            <span className="qhs-pop__item-label">{option.label}</span>
            {showBubble && option.value !== '' && (
              <span style={{ marginLeft: 8, display: 'inline-flex' }}>
                <Badge
                  size="sm"
                  tone={
                    TAG_KEYS.has(dataKey)
                      ? getTagsColor(option.value)
                      : getStatusBadge(option.value)
                  }
                >
                  {option.label}
                </Badge>
              </span>
            )}
          </button>
        );
      })}
    </FormPopover>
  );
};

SelectFilter.propTypes = {
  filterValue: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  dataKey: PropTypes.string.isRequired,
  columnData: PropTypes.shape({
    isFilterable: PropTypes.bool,
    options: PropTypes.array,
    withBubble: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

export default SelectFilter;
